import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { formatUnifiedPrice } from '../../utils/priceCalculatorUnified';
import { PRICING_CONFIG } from '../../config/pricingConfig';
import './StripePaymentModal.css'; // Réutiliser les mêmes styles

/**
 * Modal de confirmation pour les paiements en espèces / virement bancaire.
 * Structure visuellement identique au StripePaymentModal, mais sans formulaire de carte.
 * La logique d'appel API reste inchangée — le modal demande uniquement la confirmation du client.
 */
const CashConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  reservationData,
  breakdown,
  paymentMethod,
  loading = false,
}) => {
  const { t } = useTranslation(['common', 'booking', 'listing']);
  const [priceDetailsOpen, setPriceDetailsOpen] = useState(true);

  if (!open || !reservationData) return null;

  const paymentIcon = paymentMethod === 'bank_transfer' ? '🏦' : '💵';
  const paymentLabel = paymentMethod === 'bank_transfer'
    ? t('booking:sidebar.payment.methods.bankTransfer.title', 'Virement bancaire')
    : t('booking:sidebar.payment.methods.cash.title', 'Espèces');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header" style={{
          background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
          color: 'white'
        }}>
          <h2 style={{ color: 'white' }}>{t('booking:payment.cash.modalTitle', 'Confirmer la réservation')}</h2>
          <button
            className="close-button"
            onClick={onClose}
            type="button"
            aria-label={t('common:close', 'Fermer')}
            style={{ color: 'white' }}
          >
            ✕
          </button>
        </div>

        <div className="modal-body">
          <div className="stripe-payment-form">

            {/* ── Récapitulatif prix ── */}
            <div className="payment-summary">
              <div
                className="payment-summary-header"
                onClick={() => setPriceDetailsOpen(!priceDetailsOpen)}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                  flexWrap: 'nowrap',
                }}
              >
                <h3 style={{ margin: 0, paddingRight: '8px', whiteSpace: 'nowrap' }}>
                  {t('booking:payment.stripe.title', 'Récapitulatif')}
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 120, justifyContent: 'flex-end' }}>
                  <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#1e293b', marginLeft: '12px' }}>
                    {formatUnifiedPrice(breakdown?.total_amount || 0, breakdown?.currency || 'EUR')}
                  </span>
                  <span style={{ fontSize: '1.2rem', transform: priceDetailsOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
                    ▼
                  </span>
                </div>
              </div>

              <div className={`breakdown ${priceDetailsOpen ? 'open' : 'closed'}`}>

                {/* Dates */}
                {reservationData?.start_date && (
                  <div className="line-item" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '8px' }}>
                    <span style={{ fontWeight: 600 }}>{t('booking:sidebar.form.dates', 'Dates')}</span>
                    <span style={{ textAlign: 'right' }}>
                      {(() => {
                        try {
                          const s = reservationData.start_date.substring(0, 10);
                          const e = reservationData.end_date?.substring(0, 10);
                          const sd = parseISO(s);
                          const ed = e ? parseISO(e) : null;
                          if (!isValid(sd)) return `${s}${ed ? ` - ${e}` : ''}`;
                          return ed && isValid(ed)
                            ? `${format(sd, 'dd/MM/yyyy', { locale: fr })} - ${format(ed, 'dd/MM/yyyy', { locale: fr })}`
                            : format(sd, 'dd/MM/yyyy', { locale: fr });
                        } catch { return reservationData.start_date.substring(0, 10); }
                      })()}
                    </span>
                  </div>
                )}

                {/* Check-in / Check-out — propriétés immobilières */}
                {reservationData?.start_at && reservationData?.end_at && (breakdown?.days ?? 0) > 0 && (
                  <div className="line-item" style={{ color: '#6200ee', fontSize: '0.9em', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '8px' }}>
                    <span style={{ fontWeight: 600 }}>🏠 {t('listing:property.section', 'Check-in / Check-out')}</span>
                    <span style={{ textAlign: 'right', fontWeight: 600 }}>
                      {reservationData.start_at.substring(11, 16)} → {reservationData.end_at.substring(11, 16)}
                    </span>
                  </div>
                )}

                {/* Montant location */}
                <div className="line-item">
                  {(() => {
                    if ((breakdown?.days ?? 0) > 0) {
                      return <span>{t('booking:sidebar.form.subtitle', 'Réservation')} ({breakdown.days} {t('common:days')})</span>;
                    }
                    if (reservationData?.start_at && reservationData?.end_at) {
                      try {
                        const s = parseISO(reservationData.start_at);
                        const e = parseISO(reservationData.end_at);
                        if (isValid(s) && isValid(e)) {
                          return <span>{t('booking:sidebar.form.subtitle', 'Réservation')} ({format(s, 'dd/MM/yyyy HH:mm', { locale: fr })} → {format(e, 'HH:mm', { locale: fr })})</span>;
                        }
                      } catch { /* fallthrough */ }
                    }
                    return <span>{t('booking:sidebar.form.subtitle', 'Réservation')}</span>;
                  })()}
                  <span>{formatUnifiedPrice(breakdown?.rental_amount || 0, breakdown?.currency || 'EUR')}</span>
                </div>

                {/* Quantité */}
                <div className="line-item" style={{ color: '#555', fontSize: '0.9em' }}>
                  <span>{t('booking:sidebar.form.quantity', 'Quantité')}</span>
                  {(() => {
                    const qty = reservationData?.quantity_requested || 1;
                    return <span>{qty} {t('booking:sidebar.form.unit', { count: qty })}</span>;
                  })()}
                </div>

                {/* Participants (time_slot_capacity) */}
                {reservationData?.guest_count != null && (
                  <div className="line-item" style={{ color: '#555', fontSize: '0.9em' }}>
                    <span>{t('booking:sidebar.form.guestCount.label', 'Participants')}</span>
                    <span>{reservationData.guest_count}</span>
                  </div>
                )}

                {/* Places réservées (time_slot_capacity) */}
                {reservationData?.seats_requested != null && (
                  <div className="line-item" style={{ color: '#555', fontSize: '0.9em' }}>
                    <span>{t('booking:sidebar.form.seatsLabel', 'Places à réserver')}</span>
                    <span>{reservationData.seats_requested}</span>
                  </div>
                )}

                {/* Frais de service */}
                <div className="line-item">
                  <span>{t('common:serviceFee')} ({breakdown?.commission_rate_percent ?? PRICING_CONFIG.SERVICE_FEE_PERCENT}%)</span>
                  <span>{formatUnifiedPrice(breakdown?.service_fee || 0, breakdown?.currency || 'EUR')}</span>
                </div>

                {/* TVA */}
                {(breakdown?.tax_amount > 0) && (
                  <div className="line-item" style={{ color: '#888', fontSize: '0.9em', fontStyle: 'italic' }}>
                    {(() => {
                      const vr = breakdown?.vat_rate_percent ?? PRICING_CONFIG.TAX_PERCENT;
                      return <span>{t('booking:sidebar.pricing.tax', { percent: vr, defaultValue: `dont TVA (${vr}%)` })}</span>;
                    })()}
                    <span>{formatUnifiedPrice(breakdown.tax_amount, breakdown?.currency || 'EUR')}</span>
                  </div>
                )}

                {/* Sous-total */}
                <div className="line-item subtotal">
                  <span>{t('common:subtotal')}</span>
                  <span>{formatUnifiedPrice((breakdown?.rental_amount || 0) + (breakdown?.service_fee || 0), breakdown?.currency || 'EUR')}</span>
                </div>

                {/* Caution */}
                {breakdown?.deposit_amount > 0 && (
                  <div className="line-item">
                    <span>{t('booking:payment.stripe.deposit')}</span>
                    <span>{formatUnifiedPrice(breakdown.deposit_amount, breakdown?.currency || 'EUR')}</span>
                  </div>
                )}

                {/* TOTAL */}
                <div className="line-item total">
                  <span><strong>{t('booking:payment.stripe.total')}</strong></span>
                  <span><strong>{formatUnifiedPrice(breakdown?.total_amount || 0, breakdown?.currency || 'EUR')}</strong></span>
                </div>

                {/* Note caution */}
                {breakdown?.deposit_amount > 0 && (
                  <div className="deposit-notice" style={{ marginTop: '10px', padding: '8px', backgroundColor: '#f0f8ff', borderRadius: '4px', fontSize: '0.9em', color: '#666' }}>
                    💡 {t('booking:payment.stripe.depositRefundNotice', "La caution sera intégralement remboursée à l'issue de la prestation, en l'absence de dommage constaté.")}
                  </div>
                )}

                {/* Lieu */}
                {reservationData?.pickup_address && (
                  <div style={{ marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #e2e8f0', fontSize: '0.85em', color: '#555' }}>
                    <span style={{ fontWeight: 600 }}>📍 {t('booking:sidebar.payment.addresses', 'Lieu')} :</span>{' '}
                    <span>{reservationData.pickup_address}</span>
                  </div>
                )}
              </div>
            </div>

            {/* ── Mode de paiement sélectionné ── */}
            <div style={{ margin: '16px 0', padding: '14px 16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontWeight: 600, marginBottom: '4px', color: '#1e293b' }}>
                {paymentLabel}
              </div>
              <div style={{ fontSize: '0.88em', color: '#666' }}>
                {paymentMethod === 'bank_transfer'
                  ? t('booking:sidebar.payment.methods.bankTransfer.description', 'Le virement sera à effectuer avant la prise en charge du bien.')
                  : t('booking:sidebar.payment.methods.cash.description', 'Le paiement sera effectué en espèces lors de la remise du bien.')
                }
              </div>
            </div>

            {/* ── Boutons ── */}
            <div className="form-actions">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
                disabled={loading}
              >
                {t('booking:payment.stripe.buttons.cancel', 'Annuler')}
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    {t('booking:payment.stripe.processing', 'Traitement en cours...')}
                  </>
                ) : (
                  <>
                    {t('booking:payment.cash.confirmButton', 'Confirmer la réservation')}
                  </>
                )}
              </button>
            </div>

            {/* ── Info sécurité ── */}
            <div className="security-info">
              <div className="security-badge">
                 {t('booking:payment.cash.secureInfo', 'Réservation sécurisée Krillou')}
              </div>
              <p className="security-text">
                {t('booking:payment.cash.securityText', "Votre réservation est protégée par notre politique d'annulation et de garantie.")}
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default CashConfirmationModal;
